import React, { useCallback, useMemo } from 'react'
import { PaginationControlsProps } from './types'
import { usePagination } from './usePagination'

/**
 * Componente de controles de paginación
 * Incluye botones de navegación y números de página inteligentes
 */
const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
}) => {
  const { pages, showStartEllipsis, showEndEllipsis } = usePagination(
    currentPage,
    totalPages,
    5
  )

  const handlePageClick = useCallback(
    (page: number) => {
      if (page !== currentPage && !isLoading) {
        onPageChange(page)
      }
    },
    [currentPage, isLoading, onPageChange]
  )

  const isFirstPage = useMemo(() => currentPage === 1, [currentPage])
  const isLastPage = useMemo(() => currentPage === totalPages, [currentPage, totalPages])

  return (
    <div className="flex items-center gap-2 sm:gap-3" role="navigation" aria-label="Navegación de páginas">
      {/* Botón Anterior */}
      <button
        onClick={() => handlePageClick(currentPage - 1)}
        disabled={isFirstPage || isLoading}
        className="px-3 py-1.5 text-sm rounded-lg text-gray-700 border border-gray-300
                   hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300
                   disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:hover:border-gray-300
                   transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label="Página anterior"
      >
        Anterior
      </button>

      {/* Números de página */}
      <div className="flex items-center gap-1">
        {pages.map((page, index) => {
          const isCurrentPage = page === currentPage
          const isEllipsis = 
            (showStartEllipsis && index === 1) || 
            (showEndEllipsis && index === pages.length - 2)

          if (isEllipsis) {
            return (
              <span
                key={`ellipsis-${index}`}
                className="px-2 text-xs text-gray-400"
                aria-hidden="true"
              >
                ...
              </span>
            )
          }

          return (
            <button
              key={page}
              onClick={() => handlePageClick(page)}
              disabled={isLoading}
              className={`min-w-[32px] px-2 py-1 text-xs rounded-lg transition-all duration-200
                         ${
                           isCurrentPage
                             ? 'bg-blue-50 text-blue-600 font-semibold'
                             : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                         }
                         disabled:opacity-50 disabled:cursor-not-allowed
                         active:scale-95`}
              aria-label={`Ir a página ${page}`}
              aria-current={isCurrentPage ? 'page' : undefined}
            >
              {page}
            </button>
          )
        })}
      </div>

      {/* Información de página */}
      <span className="text-sm text-gray-600 whitespace-nowrap">
        {currentPage} de {totalPages}
      </span>

      {/* Botón Siguiente */}
      <button
        onClick={() => handlePageClick(currentPage + 1)}
        disabled={isLastPage || isLoading}
        className="px-3 py-1.5 text-sm rounded-lg text-gray-700 border border-gray-300
                   hover:bg-blue-50 hover:text-blue-600 hover:border-blue-300
                   disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-gray-400 disabled:hover:border-gray-300
                   transition-all duration-200 active:scale-95 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        aria-label="Página siguiente"
      >
        Siguiente
      </button>
    </div>
  )
}

export default React.memo(PaginationControls)

